import { useState, useCallback, useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useNavigate } from "react-router-dom";
import { ProColumns } from "@ant-design/pro-components";
import { Edit, Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import Layout from "../Layout";
import navigationRoutes from "../../utils/navigationRoutes";
import DynamicModuleService from "@/services/APIs/DynamicModuleService";
import { SchemaService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { toast } from "sonner";

const PP_ComparisonPropertyMaster: React.FC = () => {
  const navigate = useNavigate();

  const schemaName = "Property Master";
  const comparisonPropertySchemaName = "Compare Property";

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>("");

  // Fetch Comparison Property schema columns
  const { data: comparisonPropertyColumnData } = useQuery({
    queryKey: ["columnGridData", comparisonPropertySchemaName],
    queryFn: async () => {
      try {
        const result = await SchemaService.getColumnGridData(
          comparisonPropertySchemaName
        );
        return result ?? { grid: [] };
      } catch {
        return { grid: [] };
      }
    },
    enabled: !!comparisonPropertySchemaName,
    retry: false,
  });

  // Fetch all comparison property data to check which properties have comparison records
  const { data: comparisonPropertyData } = useQuery({
    queryKey: [
      "comparison-property-all",
      comparisonPropertySchemaName,
      comparisonPropertyColumnData,
    ],
    queryFn: async () => {
      if (!comparisonPropertyColumnData?.grid) {
        return { results: [], count: 0 };
      }
      const allColumns = comparisonPropertyColumnData.grid.map(
        (field: any) => field.field_name
      );
      if (!allColumns.map((col: string) => col.toLowerCase()).includes("id")) {
        allColumns.push("id");
      }
      if (
        !allColumns
          .map((col: string) => col.toLowerCase())
          .includes("propertymaster_id")
      ) {
        allColumns.push("propertymaster_id");
      }
      console.log("Requesting columns for comparison properties:", allColumns);
      try {
        const response = await DynamicModuleService.getGridCoByConfig(
          comparisonPropertySchemaName,
          1,
          10000,
          allColumns,
          undefined
        );

        // Handle case where response is undefined (e.g., 401 error)
        if (response === undefined || response === null) {
          console.warn(
            "Comparison Property API returned undefined, returning default structure"
          );
          return { results: [], count: 0 };
        }

        console.log("Comparison Property API response:", response);

        // Handle different response formats
        if (Array.isArray(response)) {
          return { results: response, count: response.length };
        }
        return response ?? { results: [], count: 0 };
      } catch (error: any) {
        // Try alternative schema name
        if (comparisonPropertySchemaName === "Compare Property") {
          try {
            const altResponse = await DynamicModuleService.getGridCoByConfig(
              "Comparison Property Master",
              1,
              10000,
              allColumns,
              undefined
            );
            // Handle case where altResponse is undefined (e.g., 401 error)
            if (altResponse === undefined || altResponse === null) {
              return { results: [], count: 0 };
            }
            return altResponse ?? { results: [], count: 0 };
          } catch (altError) {
            console.error("Error fetching comparison property:", altError);
            return { results: [], count: 0 };
          }
        }
        console.error("Error fetching comparison property:", error);
        return { results: [], count: 0 };
      }
    },
    enabled: !!comparisonPropertySchemaName && !!comparisonPropertyColumnData,
    retry: false,
  });

  // Create a map of propertyId -> comparison properties array
  const comparisonPropertiesByPropertyId = useMemo(() => {
    // Handle different response formats
    let comparisonProperties: any[] = [];
    if (Array.isArray(comparisonPropertyData)) {
      comparisonProperties = comparisonPropertyData;
    } else if (comparisonPropertyData?.results) {
      comparisonProperties = comparisonPropertyData.results;
    } else if (comparisonPropertyData?.data) {
      comparisonProperties = comparisonPropertyData.data;
    }

    const propertyIds = new Set<string>();
    const propertyToComparisonMap = new Map<string, any[]>();

    comparisonProperties.forEach((cp: any) => {
      // Normalize keys to lowercase for case-insensitive lookup
      const normalizedCp: any = {};
      Object.keys(cp).forEach(key => {
        normalizedCp[key.toLowerCase()] = cp[key];
      });

      // Try multiple field name variations (all lowercase now)
      const propertyId =
        normalizedCp.propertymaster_id ??
        normalizedCp.propertymasterid ??
        normalizedCp.property_master_id ??
        cp.propertymaster_id ??
        cp.propertymaster_Id ??
        cp.PropertyMasterId ??
        cp.propertymasterId ??
        cp.property_master_id ??
        cp.propertyMasterId ??
        cp.propertyMaster_ID;

      if (propertyId) {
        const idString = String(propertyId);
        propertyIds.add(idString);

        // Add to map
        if (!propertyToComparisonMap.has(idString)) {
          propertyToComparisonMap.set(idString, []);
        }
        propertyToComparisonMap.get(idString)!.push(cp);
      }
    });

    return { propertyIds, propertyToComparisonMap };
  }, [comparisonPropertyData]);

  // Create a Set of property IDs that have comparison data (for status display)
  const propertiesWithComparison = useMemo(() => {
    return comparisonPropertiesByPropertyId.propertyIds;
  }, [comparisonPropertiesByPropertyId]);

  // Calculate Final ARV for a property based on its comparison properties
  const calculatePropertyARV = useCallback(
    (property: any, comparisonProperties: any[]): number | null => {
      if (!comparisonProperties || comparisonProperties.length === 0) {
        return null;
      }

      // Find persqftprice field in comparison properties (case-insensitive)
      const persqftpriceKey =
        comparisonProperties.length > 0
          ? Object.keys(comparisonProperties[0] ?? {}).find(
              key => key.toLowerCase() === "persqftprice"
            )
          : null;

      if (!persqftpriceKey) {
        return null;
      }

      // Calculate average PPSP from comparison properties
      const validPrices: number[] = [];
      comparisonProperties.forEach((cp: any) => {
        const value = cp[persqftpriceKey];
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (typeof numValue === "number" && !isNaN(numValue)) {
          validPrices.push(numValue);
        }
      });

      if (validPrices.length === 0) {
        return null;
      }

      const avgPPSP =
        validPrices.reduce((acc, price) => acc + price, 0) / validPrices.length;

      // Get sqfootage from property (case-insensitive)
      const sqfootageKey = Object.keys(property ?? {}).find(
        key => key.toLowerCase() === "sqfootage"
      );

      if (!sqfootageKey) {
        return null;
      }

      const sqfootageValue = property[sqfootageKey];
      const sqfootageNum =
        typeof sqfootageValue === "string"
          ? parseFloat(sqfootageValue)
          : sqfootageValue;

      if (
        typeof sqfootageNum !== "number" ||
        isNaN(sqfootageNum) ||
        sqfootageNum <= 0
      ) {
        return null;
      }

      // Calculate ARV = avgPPSP × sqfootage
      const arv = avgPPSP * sqfootageNum;
      return arv;
    },
    []
  );

  // const handleDelete = useCallback(async (id: string) => {
  //   try {
  //     await DynamicModuleService.deleteItem(schemaName, id);
  //     notify.success("Property deleted successfully!");
  //     queryClient.invalidateQueries({
  //       queryKey: ["moduleData"],
  //       exact: false
  //     });
  //   } catch (error) {
  //     console.error("Delete error:", error);
  //     notify.error("Failed to delete property");
  //   }
  // }, [schemaName, queryClient]);

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.EDIT_COMPARISON_PROPERTY.replace(":id", id));
  };

  const handleExportToExcel = useCallback(async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      toast.error("Please select at least one property to export");
      return;
    }

    try {
      toast.loading("Exporting properties to Excel...", { id: "export-excel" });

      // Fetch all selected properties by their IDs
      const propertiesResponses = await Promise.all(
        selectedRowKeys.map(id =>
          DynamicModuleService.getItemById(schemaName, String(id))
        )
      );

      // Extract property data from responses (handle different response structures)
      const propertiesData = propertiesResponses.map((response: any) => {
        // Handle different response structures
        return response?.data ?? response ?? {};
      });

      // Define the columns to export with their display names
      const exportColumns = [
        { key: "persqftprice", label: "Per Sqft Price" },
        { key: "yearbuilt", label: "Year built" },
        { key: "bed", label: "Bed" },
        { key: "bath", label: "Bath" },
        { key: "sqfootage", label: "Sq. Footage" },
        { key: "rougharv", label: "Rough ARV" },
        { key: "finalarv", label: "Final ARV" },
        { key: "notes", label: "Notes" },
      ];

      // Debug: Log first property to see available fields
      if (propertiesData.length > 0) {
        console.log("[Export] Sample property data:", propertiesData[0]);
        console.log(
          "[Export] Available property keys:",
          Object.keys(propertiesData[0])
        );
      }

      // Prepare data for Excel
      const excelData = propertiesData.map((property: any) => {
        const row: any = {};
        exportColumns.forEach(col => {
          // Find the field key (case-insensitive search)
          const fieldKey = Object.keys(property ?? {}).find(
            key => key.toLowerCase() === col.key.toLowerCase()
          );

          let value: any = "";

          if (fieldKey) {
            value = property[fieldKey];
          } else {
            // Try alternative field name variations for avgppsp and arv
            if (col.key === "avgppsp") {
              const altKey = Object.keys(property ?? {}).find(
                key =>
                  key.toLowerCase() === "avg_ppsp" ||
                  key.toLowerCase() === "avgppsp" ||
                  key.toLowerCase() === "averageppsp" ||
                  key.toLowerCase() === "avgppspvalue"
              );
              if (altKey) {
                value = property[altKey];
              }
            } else if (col.key === "arv") {
              const altKey = Object.keys(property ?? {}).find(
                key =>
                  key.toLowerCase() === "arv" ||
                  key.toLowerCase() === "arv_value" ||
                  key.toLowerCase() === "arvvalue"
              );
              if (altKey) {
                value = property[altKey];
              }
            }
          }

          // Handle array/object values
          if (Array.isArray(value)) {
            value = value
              .map((item: any) => {
                if (typeof item === "object" && item !== null) {
                  return (
                    item.url ?? item.name ?? item.id ?? JSON.stringify(item)
                  );
                }
                return item;
              })
              .join(", ");
          } else if (typeof value === "object" && value !== null) {
            value =
              value.url ?? value.name ?? value.id ?? JSON.stringify(value);
          }

          // Debug for avgppsp and arv
          if (col.key === "avgppsp" || col.key === "arv") {
            console.log(
              `[Export] Property ${property.id ?? "unknown"}: ${col.label} =`,
              value,
              "from field:",
              fieldKey
            );
          }

          row[col.label] = value ?? "";
        });
        return row;
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Comparison Properties"
      );

      // Generate Excel file and download
      const fileName = `Comparison_Properties_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success(
        `Successfully exported ${selectedRowKeys.length} property(ies) to Excel`,
        { id: "export-excel" }
      );
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error(
        error?.response?.data?.message ??
          "Failed to export properties to Excel",
        { id: "export-excel" }
      );
    }
  }, [selectedRowKeys, schemaName]);

  const columns: ProColumns<any>[] = [
    {
      title: "Property Name",
      dataIndex: "propertyname",
    },
    {
      title: "Bed",
      dataIndex: "bed",
    },
    {
      title: "Bath",
      dataIndex: "bath",
    },
    {
      title: "Sq. Footage",
      dataIndex: "sqfootage",
    },
    {
      title: "Final ARV",
      dataIndex: "finalarv",
    },
    {
      title: "Status",
      width: 120,
      render: (_, record) => {
        // Try multiple variations of the ID field
        const propertyId = record.id ?? record.ID ?? record.Id ?? record._id;

        const propertyIdString = propertyId ? String(propertyId) : null;
        const hasComparison =
          propertyIdString && propertiesWithComparison.has(propertyIdString);
        const status = hasComparison ? "Completed" : "Pending";

        return (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              hasComparison
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      title: "ID",
      dataIndex: "id",
      hideInTable: true,
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <Button
          key="edit"
          variant="ghost"
          size="icon"
          onClick={() => handleEdit(record.id)}
        >
          <Edit size={16} />
        </Button>,
        // <Popconfirm
        //   key="delete"
        //   title="Are you sure you want to delete this property?"
        //   onConfirm={() => handleDelete(record.id)}
        //   okText="Yes"
        //   cancelText="No"
        // >
        //   <Button variant="ghost" size="icon">
        //     <Trash size={16} />
        //   </Button>
        // </Popconfirm>,
      ],
    },
  ];

  console.log("Columns defined:", columns);
  console.log(
    "DataIndex values being sent to API:",
    columns.filter(col => col.dataIndex).map(col => col.dataIndex)
  );

  // Fetch module data for Property Master to render table rows
  const columnsToFetch = useMemo(() => {
    return [
      "id",
      "caseno",
      "parcelid",
      "date",
      "propertyname",
      "lienamount",
      "bed",
      "bath",
      "sqfootage",
      "finalarv",
    ];
  }, []);

  const { data: moduleData, isLoading } = useQuery({
    queryKey: ["moduleData", schemaName, activeSearchTerm, columnsToFetch],
    queryFn: async () => {
      try {
        const pageSize = activeSearchTerm ? 1000 : 1000;
        const result = await DynamicModuleService.getGridCoByConfig(
          schemaName,
          1,
          pageSize,
          columnsToFetch,
          undefined
        );

        let results: any[] = [];
        const responseAny = result as any;
        if (Array.isArray(result)) {
          results = result;
        } else if (responseAny?.results && Array.isArray(responseAny.results)) {
          results = responseAny.results;
        } else if (responseAny?.data && Array.isArray(responseAny.data)) {
          results = responseAny.data;
        }

        return results;
      } catch (error) {
        console.error("Error fetching properties:", error);
        return [];
      }
    },
    enabled: !!schemaName,
  });

  const getFieldValue = (record: any, fieldName: string) => {
    const keys = Object.keys(record ?? {});
    const key = keys.find(k => k.toLowerCase() === fieldName.toLowerCase());
    return key ? record[key] : "";
  };

  const filteredData = useMemo(() => {
    if (!moduleData) {
      return [];
    }
    if (!activeSearchTerm) {
      return moduleData;
    }
    return moduleData.filter((item: any) => {
      const propertyName = getFieldValue(item, "propertyname");
      return propertyName
        .toLowerCase()
        .includes(activeSearchTerm.toLowerCase());
    });
  }, [moduleData, activeSearchTerm]);

  // Row selection helpers (to match PP_PropertyMaster UI behavior)
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = filteredData.map((item: any) => {
        const id = item.id ?? item.ID ?? item.Id ?? "";
        return String(id);
      });
      setSelectedRowKeys(allIds);
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleSelectRow = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedRowKeys(prev => [...prev, id]);
    } else {
      setSelectedRowKeys(prev => prev.filter(key => key !== id));
    }
  };

  const isRowSelected = (id: string) => {
    return selectedRowKeys.includes(id);
  };

  const isAllSelected = useMemo(() => {
    if (filteredData.length === 0) {
      return false;
    }
    return filteredData.every((item: any) => {
      const id = String(item.id ?? item.ID ?? item.Id ?? "");
      return selectedRowKeys.includes(id);
    });
  }, [filteredData, selectedRowKeys]);

  const handleSearch = useCallback(() => {
    setActiveSearchTerm(searchTerm.trim());
  }, [searchTerm]);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setActiveSearchTerm("");
  }, []);

  // Build filter object - backend doesn't support partial matching operators
  // We'll do client-side filtering instead (no backend filter used here)

  return (
    <Layout>
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground lg:text-3xl">
            Comparison Property Master
          </h1>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-[#3d3d7a] bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={handleExportToExcel}
              disabled={!selectedRowKeys || selectedRowKeys.length === 0}
            >
              Export Excel
            </Button>
            {/* <Button
              variant="outline"
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90 border-[#3d3d7a]"
            >
              Upload
            </Button>
            <Button
              className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
              onClick={() => navigate(navigationRoutes.CREATE_PROPERTY)}
            >
              + Add Property
            </Button> */}
          </div>
        </div>

        {/* Search Box */}
        <div className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by property name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="pl-10 pr-10"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                type="button"
              >
                ×
              </button>
            )}
          </div>
          <Button
            type="button"
            onClick={handleSearch}
            className="bg-[#3d3d7a] text-white hover:bg-[#3d3d7a]/90"
            disabled={!searchTerm.trim()}
          >
            <Search className="mr-2 h-4 w-4" />
            Search
          </Button>
          {activeSearchTerm && (
            <Button variant="outline" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={e => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                    />
                  </TableHead>
                  <TableHead>NO.</TableHead>
                  <TableHead>PROPERTY NAME</TableHead>
                  <TableHead>BED</TableHead>
                  <TableHead>BATH</TableHead>
                  <TableHead>SQ. FOOTAGE</TableHead>
                  <TableHead>FINAL ARV</TableHead>
                  <TableHead>STATUS</TableHead>
                  <TableHead>ACTION</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center">
                      No properties found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((property: any, index: number) => {
                    const id = String(
                      property.id ?? property.ID ?? property.Id ?? ""
                    );
                    const propertyName = getFieldValue(
                      property,
                      "propertyname"
                    );
                    const bed = getFieldValue(property, "bed");
                    const bath = getFieldValue(property, "bath");
                    const sqFootage = getFieldValue(property, "sqfootage");
                    const finalArv = getFieldValue(property, "finalarv");
                    const isSelected = isRowSelected(id);

                    const propertyIdString = id ?? null;
                    const hasComparison =
                      propertyIdString &&
                      propertiesWithComparison.has(propertyIdString);
                    const status = hasComparison ? "Completed" : "Pending";

                    return (
                      <TableRow key={id}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={e =>
                              handleSelectRow(id, e.target.checked)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-[#4a5273] focus:ring-[#4a5273]"
                          />
                        </TableCell>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="font-medium">
                          {propertyName ?? "-"}
                        </TableCell>
                        <TableCell>{bed ?? "-"}</TableCell>
                        <TableCell>{bath ?? "-"}</TableCell>
                        <TableCell>{sqFootage ?? "-"}</TableCell>
                        <TableCell>
                          {(() => {
                            // Get comparison properties for this property
                            const comparisonProps =
                              comparisonPropertiesByPropertyId.propertyToComparisonMap.get(
                                id
                              ) ?? [];

                            // Calculate ARV from comparison properties
                            const calculatedARV = calculatePropertyARV(
                              property,
                              comparisonProps
                            );

                            // Use calculated ARV if available, otherwise use stored finalArv
                            const displayARV = calculatedARV ?? finalArv;

                            if (displayARV) {
                              return typeof displayARV === "number"
                                ? `$${displayARV.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                                : displayARV;
                            }
                            return "-";
                          })()}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              hasComparison
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {status}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(id)}
                          >
                            <Edit className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PP_ComparisonPropertyMaster;
